/* Variant Media Grouping for Dawn 15.x (stable grouping + lag fix)
   - Keeps your existing grouping logic
   - Performance upgrades:
     1) Skip redundant updates when group hasn't changed
     2) Throttle slider refresh (Dawn slider recalculation is expensive)
     3) rAF used to keep UI smooth
*/
(() => {
  const HIDDEN_CLASS = 'vmgh-hidden';

  // Set this to false if you want MAX speed and can live without slider recalcs
  const ENABLE_SLIDER_REFRESH = true;

  function normalizeList(str) {
    return (str || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }

  function debounce(fn, delay = 80) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), delay);
    };
  }

  function applyHiddenCSSOnce() {
    if (document.getElementById('vmgh-style')) return;
    const style = document.createElement('style');
    style.id = 'vmgh-style';
    style.textContent = `.${HIDDEN_CLASS}{display:none!important;}`;
    document.head.appendChild(style);
  }

  async function fetchProductJson(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}products/${handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`VMGH: product JSON fetch failed (${res.status})`);
    return res.json();
  }

  function getSelectedOptionValues(scopeEl) {
    const variantSelects = scopeEl.querySelector('variant-selects');
    if (!variantSelects) return [];

    const selects = Array.from(variantSelects.querySelectorAll('select'));
    const radios = Array.from(
      variantSelects.querySelectorAll('fieldset input[type="radio"]:checked')
    );

    return [
      ...selects.map(s => s.value).filter(Boolean),
      ...radios.map(r => r.value).filter(Boolean),
    ];
  }

  function buildGroups(product, optionNames) {
    const option = product.options.find(o => optionNames.includes(o.name));
    if (!option) return null;

    const positions = {};
    option.values.forEach(value => {
      const variant = product.variants.find(v => v.options[option.position - 1] === value);
      if (variant?.featured_image?.position) positions[value] = variant.featured_image.position;
    });

    const sorted = Object.entries(positions).sort((a, b) => a[1] - b[1]);
    if (!sorted.length) return null;

    const groups = {};
    sorted.forEach(([value, startPos], idx) => {
      const nextPos = sorted[idx + 1]?.[1] ?? (product.media.length + 1);
      groups[value] = product.media
        .filter((_, i) => {
          const pos = i + 1;
          return pos >= startPos && pos < nextPos;
        })
        .map(m => String(m.id));
    });

    return { groups };
  }

  function endsWithMediaId(str, mediaId) {
    return typeof str === 'string' && str.endsWith(`-${mediaId}`);
  }

  function tagGalleryLis(gallery, groups) {
    const groupValues = Object.keys(groups);
    if (!groupValues.length) return;

    const nodes = gallery.querySelectorAll('[data-media-id], [data-target]');

    nodes.forEach(node => {
      const mediaIdAttr = node.getAttribute('data-media-id');
      const targetAttr = node.getAttribute('data-target');
      const li = node.closest('li') || node;

      for (const groupValue of groupValues) {
        for (const id of groups[groupValue]) {
          const match =
            endsWithMediaId(mediaIdAttr, id) ||
            endsWithMediaId(targetAttr, id) ||
            mediaIdAttr === id ||
            targetAttr === id;

          if (match) {
            li.dataset.vmghGroup = groupValue;
            return;
          }
        }
      }
    });
  }

  // Throttled slider refresh to reduce jank
  function makeSliderRefresher() {
    let lastRun = 0;
    let scheduled = false;

    return (gallery) => {
      if (!ENABLE_SLIDER_REFRESH) return;

      const now = Date.now();
      // Don’t refresh more often than every 120ms
      if (now - lastRun < 120) {
        if (scheduled) return;
        scheduled = true;
        setTimeout(() => {
          scheduled = false;
          requestAnimationFrame(() => {
            gallery.querySelectorAll('slider-component').forEach(slider => {
              if (typeof slider.initPages === 'function') slider.initPages();
              if (typeof slider.update === 'function') slider.update();
            });
            lastRun = Date.now();
          });
        }, 120 - (now - lastRun));
        return;
      }

      requestAnimationFrame(() => {
        gallery.querySelectorAll('slider-component').forEach(slider => {
          if (typeof slider.initPages === 'function') slider.initPages();
          if (typeof slider.update === 'function') slider.update();
        });
        lastRun = Date.now();
      });
    };
  }

  async function init(root) {
    const enabled = root.dataset.vmghEnabled === 'true';
    if (!enabled) return;

    const handle = root.dataset.vmghHandle;
    const optionNames = normalizeList(root.dataset.vmghOptionNames);
    if (!handle || !optionNames.length) return;

    const section = root.closest('.shopify-section') || document;

    // Prevent duplicate init
    if (section.dataset.vmghInitialized === 'true') return;
    section.dataset.vmghInitialized = 'true';

    const gallery = section.querySelector('media-gallery');
    if (!gallery) {
      console.warn('VMGH: media-gallery not found in this section');
      return;
    }

    applyHiddenCSSOnce();

    let product;
    try {
      product = await fetchProductJson(handle);
    } catch (e) {
      console.error(e);
      return;
    }

    const built = buildGroups(product, optionNames);
    if (!built) {
      console.warn('VMGH: groups not built. Check option name + featured images + ordering.');
      return;
    }

    tagGalleryLis(gallery, built.groups);

    const refreshSlider = makeSliderRefresher();

    // ✅ KEY LAG FIX: only re-render when the selected group actually changes
    let lastGroup = null;

    const update = debounce(() => {
      const selected = getSelectedOptionValues(section);
      const selectedGroup = selected.find(v => built.groups[v]);
      if (!selectedGroup) return;

      // Skip redundant work
      if (selectedGroup === lastGroup) return;
      lastGroup = selectedGroup;

      const items = gallery.querySelectorAll('li[data-vmgh-group]');
      items.forEach(li => {
        if (li.dataset.vmghGroup === selectedGroup) li.classList.remove(HIDDEN_CLASS);
        else li.classList.add(HIDDEN_CLASS);
      });

      // Throttled slider refresh (much less jank)
      refreshSlider(gallery);
    }, 80);

    // Run once
    update();

    const hasPubSub =
      typeof window.subscribe === 'function' &&
      window.PUB_SUB_EVENTS &&
      window.PUB_SUB_EVENTS.optionValueSelectionChange;

    if (hasPubSub) {
      window.subscribe(window.PUB_SUB_EVENTS.optionValueSelectionChange, update);
    }

    section.addEventListener('change', (e) => {
      if (e.target.closest && e.target.closest('variant-selects')) update();
    });

    // Keep popstate off for speed
    // window.addEventListener('popstate', update);
  }

  function boot() {
    document.querySelectorAll('[data-vmgh-root]').forEach(root => init(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
